<template>
  <div>
    <FormGroup>
      <Text variant="h2">Property</Text>

      <FormField label="Property type" :error="form.fieldErrors('propertyType')">
        <Select v-model="form.fields.propertyType">
          <option v-for="option in propertyTypeOptions" :key="option" :value="option">{{ option }}</option>
        </Select>
      </FormField>

      <FormField
        v-if="isApartmentNumberFieldRequired"
        label="Apartment number"
        :error="form.fieldErrors('apartmentNumber')"
      >
        <Input v-model="form.fields.apartmentNumber" placeholder="What is the apartment number?">
          <template #prefix>#</template>
        </Input>
      </FormField>

      <FormField label="Architectural style" :error="form.fieldErrors('architectureStyle')">
        <Select v-model="form.fields.architectureStyle">
          <option v-for="option in architectureStyleOptions" :key="option" :value="option">{{ option }}</option>
        </Select>
      </FormField>

      <FormField label="Year built" :error="form.fieldErrors('yearBuilt')">
        <Input v-model="form.fields.yearBuilt" type="number" placeholder="What year was the property built?" />
      </FormField>

      <FormField label="Total number of floors" :error="form.fieldErrors('numberOfFloors')">
        <Input v-model="form.fields.numberOfFloors" type="number" min="0" name="numberOfFloors" />
      </FormField>

      <FormField label="Which floor is the property on?" :error="form.fieldErrors('apartmentFloor')">
        <Input
          v-model="form.fields.apartmentFloor"
          type="number"
          min="0"
          :max="form.fields.numberOfFloors"
          name="apartmentFloor"
        />
      </FormField>

      <Text variant="h2">Interior</Text>

      <FormField label="Property Size" :error="form.fieldErrors('squareFeet')">
        <Input
          v-model="form.fields.squareFeet"
          type="number"
          min="0"
          name="squareFeet"
          placeholder="Enter a whole number, e.g. 850"
          @keydown="forbidCharacters"
        >
          <template #suffix>ft²</template>
        </Input>
      </FormField>

      <FormField v-if="isBedroomFieldVisible" label="Bedrooms" direction="row" :error="form.fieldErrors('bedrooms')">
        <InputStepper v-model="form.fields.bedrooms" :min="1" name="bedrooms" />
      </FormField>

      <FormField label="Full bathrooms" direction="row" :error="form.fieldErrors('fullBathrooms')">
        <InputStepper v-model="form.fields.fullBathrooms" :min="0" name="fullBathrooms" />
      </FormField>

      <FormField label="Half bathrooms" direction="row" :error="form.fieldErrors('halfBathrooms')">
        <InputStepper v-model="form.fields.halfBathrooms" :min="0" name="halfBathrooms" />
      </FormField>
    </FormGroup>
  </div>
</template>

<script setup lang="ts">
import { requiredIf } from '@vuelidate/validators';
import { computed, watch } from 'vue';

import { FormField, FormGroup, Select, Input, InputStepper, useForm, Text } from '@tallo/design-system';
import { PropertyType, ArchitectureStyle, PropertySpecificationsDto, PropertyLocationDto } from '@tallo/property';

import { SaveLocationDto } from '../../dtos/save-location.dto.interface';
import { SavePropertySpecificationsDto } from '../../dtos/save-property-specifications.dto.interface';
import { usePropertyStore } from '../../stores/property.store';
import { isApartmentNumberRequired } from '../../utils/apartment-number-required';
import { isBedroomFieldRequired } from '../../utils/bedroom-field-required';

const propertyTypeOptions = Object.values(PropertyType);
const architectureStyleOptions = Object.values(ArchitectureStyle);
const propertyStore = usePropertyStore();
// A unit in a building keeps the number it was added with, since the building owns its address
const isApartmentNumberFieldRequired = computed(
  () => !propertyStore.property?.buildingId && isApartmentNumberRequired(form.fields.propertyType),
);
const isBedroomFieldVisible = computed(() => isBedroomFieldRequired(form.fields.propertyType));
const form = useForm<SavePropertySpecificationsDto & Pick<SaveLocationDto, 'apartmentNumber'>>(
  {
    apartmentNumber: null,
    propertyType: null,
    architectureStyle: null,
    yearBuilt: null,
    apartmentFloor: 1,
    numberOfFloors: 1,
    squareFeet: null,
    bedrooms: 1,
    fullBathrooms: null,
    halfBathrooms: null,
  },
  {
    apartmentNumber: {
      required: requiredIf((): boolean => isApartmentNumberFieldRequired.value),
    },
  },
);

async function save() {
  return await form.submit((fields) => {
    const apartmentNumber = isApartmentNumberFieldRequired.value ? fields.apartmentNumber : null;

    const specificationsData = {
      ...fields,
      bedrooms: isBedroomFieldVisible.value ? fields.bedrooms : 0,
    };

    return Promise.all([
      propertyStore.saveSpecifications(specificationsData),
      ...(propertyStore.property?.buildingId ? [] : [propertyStore.saveLocation({ apartmentNumber })]),
    ]);
  });
}

function populateSpecificationFields(specifications?: PropertySpecificationsDto) {
  Object.assign(form.fields, {
    propertyType: specifications?.propertyType || form.fields.propertyType,
    architectureStyle: specifications?.architectureStyle || form.fields.architectureStyle,
    yearBuilt: specifications?.yearBuilt ?? form.fields.yearBuilt,
    apartmentFloor: specifications?.apartmentFloor ?? form.fields.apartmentFloor,
    numberOfFloors: specifications?.numberOfFloors ?? form.fields.numberOfFloors,
    squareFeet: specifications?.squareFeet ?? form.fields.squareFeet,
    bedrooms: specifications?.bedrooms || form.fields.bedrooms,
    fullBathrooms: specifications?.fullBathrooms ?? form.fields.fullBathrooms,
    halfBathrooms: specifications?.halfBathrooms ?? form.fields.halfBathrooms,
  });
}

function populateLocationFields(location?: PropertyLocationDto) {
  Object.assign(form.fields, { apartmentNumber: location?.apartmentNumber || form.fields.apartmentNumber });
}

function forbidCharacters(event: KeyboardEvent) {
  if (event.key === '.' || event.key === ',' || event.key.toLowerCase() === 'e') {
    event.preventDefault();
  }
}

watch(() => propertyStore.property?.specifications, populateSpecificationFields, { immediate: true });
watch(() => propertyStore.property?.location, populateLocationFields, { immediate: true });

defineExpose({ save, form });
</script>
